import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { nsCustomization } from '@/app/i18n/namespaces';
import CUSTOMIZATION_URL_ACTIONS from '../../enums/CustomizationUrlActions';
// Components
import Entity from '@/common-utils/components/renders/entity/Entity';
import { Button } from '@/common-utils/components/ui/button';
import { Input } from '@/common-utils/components/ui/input';
import { Skeleton } from '@/common-utils/components/ui/skeleton';
import ICONS from '@/common-utils/enums/icons';
// Assets
import placeholder from 'url:../../../../../../../resource/images/logo1.png';
import useColorsStore from '@/app/store/colors';
import PosTerminalPreview from './PosTerminalPreview';
import isFalsy from '@/common-utils/functions/isFalsy';
/**
 * ColorLogoSection - Handles color selection and logo upload/preview
 *
 * @param {object} props - Component props
 * @param {object} props.customization - The customization object
 * @param {string} props.action - Current page action (show, duplicate, etc.)
 * @param {boolean} props.isPackage - Whether the customization is a published package
 * @param {boolean} props.isLoading - Loading state
 * @param {string} props.color - Current color value (#RRGGBB)
 * @param {string} props.logo - Current logo (url or base64)
 * @param {object} props.locale - Current locale (key: value)
 * @param {Function} props.onColorChange - Callback when color changes
 * @param {Function} props.onLogoChange - Callback when logo changes
 * @param {boolean} props.isScreenSaver
 * @param {array} props.checkedOptions - array of checked options
 * @param {Function} props.setIsDirtyColorAndIcon - Callback when dirty color and icon changes
 * @returns {JSX.Element} ColorLogoSection component
 */
function ColorLogoSection({
    customization,
    action = '',
    isPackage = false,
    isScreenSaver = false,
    isLoading = false,
    checkedOptions = [],
    color = '',
    logo = '',
    locale = 'fr-FR',
    onColorChange,
    onLogoChange,
    setIsDirtyColorAndIcon
}) {

    // Store
    const dynamicBackgroundColor = useColorsStore((state) => state.dynamicBackgroundColor);

    // State
    const [fileError, setFileError] = useState('');
    const [fileSelectionKey, setFileSelectionKey] = useState(0);

    // Constants
    const fileInputRef = useRef(null);
    const isDuplicateAction = action === CUSTOMIZATION_URL_ACTIONS.DUPLICATE;
    const isDisabled = action === CUSTOMIZATION_URL_ACTIONS.SHOW || (isPackage && !isDuplicateAction);
    // Trust parent-provided color; fall back to user/org color (no white default)
    const safeColor = color || dynamicBackgroundColor;

    // Hooks
    // Updates the isDirtyColorAndIcon state to true when the color or logo changes
    useEffect(() => {
        const customLogo = isFalsy(customization?.logo) ? '' : customization?.logo;
        if (color !== customization?.color
            || logo !== customLogo) {
            setIsDirtyColorAndIcon(true);
        } else {
            setIsDirtyColorAndIcon(false);
        }
    }, [customization, color, logo]);

    // Functions
    const handleColorTextChange = (value) => {
        let nextValue = value;
        if (nextValue && !nextValue.startsWith('#')) {
            nextValue = `#${nextValue}`;
        }
        if (nextValue.length <= 7) {
            onColorChange(nextValue);
        }
    };

    const handleFileChange = (event) => {
        const selectedFile = event.target.files?.[0];
        if (!selectedFile) {
            return;
        }

        // Limit: 200KB (same rule as legacy component)
        if (selectedFile.size / 1024 > 200) {
            setFileError('file_upload.default.invalid_size_file');
            onLogoChange('');
            setFileSelectionKey((k) => k + 1);
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            const result = e.target?.result || '';
            setFileError('');
            onLogoChange(result);
        };
        reader.readAsDataURL(selectedFile);
        setFileSelectionKey((k) => k + 1);
    };

    const handleRemoveLogo = () => {
        onLogoChange('');
        setFileError('');
        setFileSelectionKey((k) => k + 1);
    };

    // Loading skeleton
    if (isLoading) {
        return (
            <div className='flex flex-col gap-5 py-2 px-4 lg:px-8'>
                <div className='flex flex-col md:flex-row md:items-center gap-2 md:gap-6'>
                    <Skeleton className='h-4 w-40 bg-gray-200' />
                    <Skeleton className='h-10 w-full md:w-72 bg-gray-200' />
                </div>
                <div className='flex flex-col md:flex-row md:items-center gap-2 md:gap-6'>
                    <Skeleton className='h-4 w-40 bg-gray-200' />
                    <div className='flex items-center gap-3 w-full md:w-96'>
                        <Skeleton className='h-24 w-40 bg-gray-200' />
                        <Skeleton className='h-10 w-32 bg-gray-200' />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className='flex flex-col items-center gap-8 py-2 px-4 lg:px-8'>
            <div className='flex flex-col lg:flex-row items-center justify-center gap-8 lg:gap-16 w-full'>
                {/* Color + Logo column (first on mobile and left on large screens) */}
                <div className='order-1 lg:order-1 flex flex-col gap-6 w-full lg:w-1/2 items-center'>
                    <div className='flex flex-col gap-3 items-center'>
                        <div className='flex items-center gap-3'>
                            <Input
                                disabled={isDisabled}
                                type='color'
                                value={safeColor}
                                onChange={(e) => onColorChange(e.target.value)}
                                className='w-16 h-10 p-1 cursor-pointer'
                            />
                            <Input
                                disabled={isDisabled}
                                type='text'
                                value={color || safeColor}
                                onChange={(e) => handleColorTextChange(e.target.value)}
                                maxLength={7}
                                placeholder='#000000'
                                className='w-28'
                            />
                        </div>
                    </div>

                    <div className='flex flex-col items-center gap-3'>
                        <div
                            className='h-28 w-[188px] rounded-md border border-gray-200 bg-white flex items-center justify-center overflow-hidden shadow-sm'
                            style={{ backgroundColor: safeColor }}
                        >
                            {
                                logo?.length > 0 &&
                                <img
                                    src={logo}
                                    alt='Logo preview'
                                    className='max-h-16 max-w-[110px] object-contain'
                                    onError={(e) => {
                                        e.currentTarget.onerror = null;
                                        e.currentTarget.src = placeholder;
                                    }}
                                />
                            }
                        </div>
                        <div className='flex gap-2'>
                            <input
                                key={fileSelectionKey}
                                ref={fileInputRef}
                                type='file'
                                accept='.png,.jpg,.jpeg'
                                className='hidden'
                                onChange={handleFileChange}
                                disabled={isDisabled}
                            />
                            <Button
                                type='button'
                                variant='secondary'
                                icon={ICONS.UPLOAD}
                                onClick={() => fileInputRef.current?.click()}
                                disabled={isDisabled}
                            />
                            {
                                logo &&
                                <Button
                                    type='button'
                                    variant='danger-outline'
                                    icon={ICONS.CANCEL}
                                    onClick={handleRemoveLogo}
                                    disabled={isDisabled}
                                />
                            }
                        </div>
                        <div className='text-sm text-gray-600 space-y-1 text-left'>
                            <p><Entity entity='details.fields.logo.text1' namespace={nsCustomization} /></p>
                            <p><Entity entity='details.fields.logo.text2' namespace={nsCustomization} /></p>
                        </div>
                        {fileError && (
                            <p className='text-danger text-sm'>
                                <Entity entity='details.fields.logo.error' namespace={nsCustomization} />
                            </p>
                        )}
                    </div>
                </div>

                {/* Preview column (right on large screens) */}
                <div className='order-2 lg:order-2 flex flex-col items-center gap-8 w-full lg:w-1/2'>
                    <PosTerminalPreview
                        color={safeColor}
                        logo={logo}
                        locale={locale}
                        isScreenSaver={isScreenSaver}
                        checkedOptions={checkedOptions}
                    />
                </div>
            </div>
        </div>
    );
}

ColorLogoSection.propTypes = {
    customization: PropTypes.object,
    action: PropTypes.string,
    isPackage: PropTypes.bool,
    isScreenSaver: PropTypes.bool,
    isLoading: PropTypes.bool,
    color: PropTypes.string,
    checkedOptions: PropTypes.array,
    logo: PropTypes.string,
    locale: PropTypes.object,
    onColorChange: PropTypes.func,
    onLogoChange: PropTypes.func,
    setIsDirtyColorAndIcon: PropTypes.func
};

export default ColorLogoSection;


